import { useEffect, useState, useRef } from 'react'
import AuthService from './services/AuthService'
import { resizeImageToDataURL, dataUrlToFile, LOGO_MAX_SIZE } from './lib/imageUtils'

// `preview` is a data URL (freshly picked) or a URL (the stored logo); `onFile` receives the
// chosen File, `onUrl` a pasted address (resolved by the API, see /api/admin/logo-fetch) and
// resolves to false when the import failed, `onRemove` clears the logo. Shared by the create
// and edit user modals. Kept at module scope so it is not re-created — and the inputs
// remounted — on every render.
function LogoField({ preview, onFile, onUrl, onRemove, busy }) {
  const [url, setUrl] = useState('')

  const importUrl = async () => {
    const trimmed = url.trim()
    if (!trimmed || busy) return
    if (await onUrl(trimmed)) setUrl('')
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">
        Logo client{' '}
        <span className="normal-case font-normal text-[10px]">
          (optionnel, PNG/JPG/WebP/SVG, réduit à {LOGO_MAX_SIZE}px)
        </span>
      </label>
      <div className="flex items-center gap-3">
        {preview && (
          <img
            src={preview}
            alt=""
            className="h-[30px] max-w-[90px] object-contain border border-[#77726e]/20 bg-white p-1"
          />
        )}
        <input
          type="file"
          accept="image/*,.svg"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
          className="flex-1 text-xs text-[#77726e] file:mr-2 file:px-2 file:py-1 file:rounded file:border file:border-[#77726e]/30 file:bg-[#f6f3ee] file:text-xs file:text-[#2f3126]"
        />
        {preview && (
          <button
            type="button"
            onClick={onRemove}
            className="px-2 py-1 rounded border border-red-200 text-[10px] text-red-600 bg-white hover:bg-red-50 transition"
          >
            Retirer
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); importUrl() } }}
          placeholder="…ou coller un lien vers l’image"
          className="flex-1 px-2 py-1 rounded border border-[#77726e]/30 bg-[#f6f3ee] text-xs text-[#2f3126] placeholder:text-[#77726e]/60"
        />
        <button
          type="button"
          onClick={importUrl}
          disabled={busy || !url.trim()}
          className="px-2 py-1 rounded border border-[#4e6498]/40 text-[10px] font-semibold text-[#4e6498] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition disabled:opacity-40"
        >
          {busy ? '…' : 'Importer'}
        </button>
      </div>
    </div>
  )
}

export default function Admin({ onBack }) {
  const auth = AuthService
  const currentUser = auth.getCurrentUser()

  if (!currentUser || currentUser.role !== 'admin') {
    return <div className="p-8 text-[#77726e]">Accès administrateur requis</div>
  }

  const [companies, setCompanies] = useState([])
  const [users, setUsers] = useState({ users: {}, companyFiles: {} })
  const [result, setResult] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [analysisTarget, setAnalysisTarget] = useState('entreprise')
  const [isProcessing, setIsProcessing] = useState(false)
  const [logoBusy, setLogoBusy] = useState(false)
  const [analysisId, setAnalysisId] = useState(null)
  const [logs, setLogs] = useState([])
  const [showUserModal, setShowUserModal] = useState(false)
  const [newUser, setNewUser] = useState({ username: '', password: '', company: '', role: 'user', logoData: null })
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [selectedCompanyFile, setSelectedCompanyFile] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [deletingFiles, setDeletingFiles] = useState(new Set())
  const [userToDelete, setUserToDelete] = useState(null)
  const [fileToDelete, setFileToDelete] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editUser, setEditUser] = useState({ username: '', password: '', role: 'user', company: '', logoData: undefined, currentLogo: null })
  const [userFormError, setUserFormError] = useState(null)
  const pollingRef = useRef(null)
  const lastLogCountRef = useRef(0)

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date().toLocaleTimeString() }])
  }

  const filenameToCompanyName = (filename) => filename?.split('_')[0] || filename

  const getScoreColor = (score) => {
    if (score >= 80) return '#39774f'
    if (score >= 50) return '#de6433'
    return '#9c2128'
  }

  useEffect(() => {
    loadCompanies()
    loadUsers()
    return () => { if (pollingRef.current) clearInterval(pollingRef.current) }
  }, [])

  const loadCompanies = async () => {
    await auth.refreshData()
    const organized = auth.getOrganizedData()
    const flat = []
    Object.entries(organized).forEach(([company, byDate]) => {
      Object.values(byDate).forEach(entry => {
        flat.push({ company, filename: entry.filename, score: 0, date: null, categories: [] })
      })
    })
    setCompanies(flat)
  }

  const filteredAndSortedCompanies = companies
    .filter(c => c.filename.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc' ? a.filename.localeCompare(b.filename) : b.filename.localeCompare(a.filename)
      }
      return sortOrder === 'asc' ? 1 : -1
    })

  const loadUsers = async () => {
    const res = await auth.fetchWithAuth('/api/admin/users')
    const json = await res.json()
    if (json.success) setUsers(json.data)
  }

  const addUser = async () => {
    setUserFormError(null)
    const res = await auth.fetchWithAuth('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.success) {
      setUserFormError(json.error || 'Création impossible')
      return
    }
    setShowUserModal(false)
    setNewUser({ username: '', password: '', company: '', role: 'user', logoData: null })
    loadUsers()
  }

  const deleteUser = async (username) => {
    await auth.fetchWithAuth(`/api/admin/users/${username}`, { method: 'DELETE' })
    loadUsers()
  }

  const updateUser = async () => {
    setUserFormError(null)
    const { username, password, role, company, logoData } = editUser
    const body = { role, company }
    if (password) body.password = password
    // undefined = untouched, null = remove, string = replace
    if (logoData !== undefined) body.logoData = logoData
    const res = await auth.fetchWithAuth(`/api/admin/users/${username}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.success) {
      setUserFormError(json.error || 'Modification impossible')
      return
    }
    setShowEditModal(false)
    setEditUser({ username: '', password: '', role: 'user', company: '', logoData: undefined, currentLogo: null })
    loadUsers()
  }

  const pickLogo = async (file, apply) => {
    if (!file) return
    setUserFormError(null)
    try {
      apply(await resizeImageToDataURL(file, LOGO_MAX_SIZE))
    } catch (e) {
      setUserFormError(e.message || 'Image illisible')
    }
  }

  // The API only relays the bytes — fetching the URL from here would taint the canvas and
  // break toDataURL(). Resizing and PNG encoding still happen in the browser, exactly as
  // for a hand-picked file.
  const pickLogoFromUrl = async (url, apply) => {
    setUserFormError(null)
    setLogoBusy(true)
    try {
      const res = await auth.fetchWithAuth('/api/admin/logo-fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      // A non-JSON body means the response did not come from the route itself — an Express
      // 404 for an API that predates it, or a proxy error page. Surfacing the status keeps
      // that from reading as "the image could not be downloaded".
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Téléchargement impossible (HTTP ${res.status})`)
      }
      apply(await resizeImageToDataURL(dataUrlToFile(json.data.dataUrl), LOGO_MAX_SIZE))
      return true
    } catch (e) {
      setUserFormError(e.message || 'Image illisible')
      return false
    } finally {
      setLogoBusy(false)
    }
  }

  const togglePublic = async (username, currentValue) => {
    await auth.fetchWithAuth(`/api/admin/users/${username}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ public: !currentValue }),
    })
    loadUsers()
  }

  // Streams a background run's logs into the console. Shared by the full analysis and the
  // pros/cons completion, which both report through /api/analysis-status/:id.
  const pollAnalysis = (id, label = 'Analyse') => {
    setAnalysisId(id)
    pollingRef.current = setInterval(async () => {
      const r = await auth.fetchWithAuth(`/api/analysis-status/${id}`)
      const d = await r.json()
      if (d.logs?.length > lastLogCountRef.current) {
        d.logs.slice(lastLogCountRef.current).forEach(line => addLog(line))
        lastLogCountRef.current = d.logs.length
      }
      if (d.status === 'completed' || d.status === 'failed') {
        clearInterval(pollingRef.current)
        setIsProcessing(false)
        setAnalysisId(null)
        loadCompanies()
        if (d.status === 'failed') {
          addLog(`${label} échouée (code: ${d.exitCode ?? '?'})`, 'error')
        } else {
          addLog(`${label} terminée`, 'success')
        }
      }
    }, 3000)
  }

  const startAsyncAnalysis = async () => {
    if (!companyName.trim()) return
    setIsProcessing(true)
    lastLogCountRef.current = 0
    addLog(`Analyse lancée : ${companyName}`)
    const res = await auth.fetchWithAuth('/api/start-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName, target: analysisTarget }),
    })
    const json = await res.json()
    pollAnalysis(json.analysisId)
  }

  // Fills in missing pros/cons and regenerates the synthesis — no question is re-asked.
  const completeAnalysis = async (filename) => {
    if (isProcessing) return
    setIsProcessing(true)
    lastLogCountRef.current = 0
    setLogs([])
    addLog(`Complément pros/cons + synthèse : ${filename}`)
    const res = await auth.fetchWithAuth('/api/complete-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename }),
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.analysisId) {
      addLog(json.error || 'Complément impossible', 'error')
      setIsProcessing(false)
      return
    }
    pollAnalysis(json.analysisId, 'Complétion')
  }

  const handleGenerateSummary = async (filename) => {
    await auth.fetchWithAuth('/api/generate-summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename }),
    })
    loadCompanies()
  }

  const openJson = async (filename) => {
    const res = await auth.fetchWithAuth(`/api/analysis/file/${encodeURIComponent(filename)}`)
    const json = await res.json()
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  const deleteCompany = async (filename) => {
    setDeletingFiles(prev => new Set(prev).add(filename))
    await auth.fetchWithAuth('/api/remove-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename }),
    })
    setDeletingFiles(prev => { const s = new Set(prev); s.delete(filename); return s })
    loadCompanies()
  }

  const assignAnalysisToUser = async (filename, username) => {
    await auth.fetchWithAuth('/api/users/assign-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, username }),
    })
    loadUsers()
  }

  const unassignAnalysis = async (filename, company) => {
    await auth.fetchWithAuth('/api/users/unassign-analysis', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename, company }),
    })
    loadUsers()
  }

  const inputCls = "w-full border border-[#77726e]/40 rounded-md px-3 py-2 text-sm text-[#2f3126] bg-[#f6f3ee] focus:outline-none focus:ring-2 focus:ring-[#4e6498]/30 focus:border-[#4e6498]"
  const btnPrimary = "px-4 py-2 rounded-md bg-[#2f3126] text-[#f6f3ee] text-sm font-semibold hover:bg-[#77726e] transition"
  const btnSecondary = "px-4 py-2 rounded-md border border-[#77726e]/40 text-sm text-[#2f3126] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition"
  const cardCls = "bg-white border border-[#77726e]/30 rounded-[0.625rem] shadow-sm"

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d1d6d8' }}>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#77726e]/20" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className={btnSecondary}>← Retour</button>
            <h1 className="text-2xl font-extrabold text-[#2f3126] font-title">Administration</h1>
          </div>
          <a href="https://opsci.ai" target="_blank" rel="noopener noreferrer">
            <img src="./opsci-logo-hg.png" alt="OpSci" className="h-7 opacity-70 hover:opacity-100 transition" />
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Entreprises', value: companies.length, color: '#4e6498' },
            { label: 'Scores ≥80%', value: companies.filter(c => c.score >= 80).length, color: '#39774f' },
            { label: 'Pipeline', value: 'ACTIF', color: '#a4aa35' },
            { label: 'En cours', value: isProcessing ? '1' : '0', color: '#de6433' },
            { label: 'Utilisateurs', value: Object.keys(users.users || {}).length, color: '#9c2128' },
          ].map(({ label, value, color }) => (
            <div key={label} className={cardCls + ' p-4'}>
              <div className="text-2xl font-extrabold font-title" style={{ color }}>{value}</div>
              <div className="text-xs text-[#77726e] mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* Grille principale */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

          {/* Contrôleur d'analyse */}
          <div className={cardCls + ' xl:col-span-2'}>
            <div className="px-5 pt-4 pb-3 border-b border-[#77726e]/15">
              <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider">Lancer une analyse</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">Nom de l'entreprise</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className={inputCls}
                  placeholder="Ex: Apple, Nike..."
                  disabled={isProcessing}
                  onKeyDown={(e) => e.key === 'Enter' && !isProcessing && startAsyncAnalysis()}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">
                  Cible d&apos;analyse
                  <span className="ml-1 text-[#77726e]/60 normal-case font-normal">(questions_{'{target}'}.csv)</span>
                </label>
                <input
                  type="text"
                  value={analysisTarget}
                  onChange={(e) => setAnalysisTarget(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  className={inputCls}
                  placeholder="entreprise"
                  disabled={isProcessing}
                />
              </div>

              <div className="bg-[#f6f3ee] border border-[#77726e]/20 rounded-md p-3">
                <p className="text-xs font-semibold text-[#2f3126] mb-1">Catégories analysées (questions_entreprise.csv) :</p>
                <div className="text-xs text-[#77726e] grid grid-cols-2 gap-0.5">
                  {['Identité','Produits & services','Expérience client','Innovation','Éthique tech','Cybersécurité','Supply chain','Environnement','D&I','Marque employeur','Gouvernance','Performance financière','Transparence & lobbying','Affaires & rumeurs','Crises & résilience'].map(c => (
                    <span key={c}>• {c}</span>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={startAsyncAnalysis}
                  disabled={isProcessing || !companyName.trim()}
                  className="flex-1 px-4 py-2 rounded-md bg-[#39774f] text-white text-sm font-semibold hover:bg-[#2f5c3c] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Analyse...
                    </span>
                  ) : 'Lancer'}
                </button>
                <button onClick={() => setLogs([])} className={btnSecondary}>Clear logs</button>
              </div>

              {analysisId && (
                <div className="bg-[#f6f3ee] border border-[#77726e]/20 rounded-md p-3 text-xs text-[#2f3126]">
                  <span className="font-semibold">ID :</span> {analysisId} — <span className="font-semibold">Entreprise :</span> {companyName}
                </div>
              )}
            </div>
          </div>

          {/* Utilisateurs */}
          <div className={cardCls}>
            <div className="px-5 pt-4 pb-3 border-b border-[#77726e]/15 flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider">Utilisateurs</h3>
              <button onClick={() => { setUserFormError(null); setShowUserModal(true) }} className="px-2.5 py-1 rounded-full bg-[#2f3126] text-[#f6f3ee] text-xs font-semibold hover:bg-[#77726e] transition">+ Ajouter</button>
            </div>
            <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
              {Object.entries(users.users || {}).map(([username, userData]) => (
                <div key={username} className="bg-[#f6f3ee] border border-[#77726e]/20 rounded-md px-3 py-2 space-y-1.5">
                  {/* Row 1: identity */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {userData.logo && (
                      <img
                        src={`/api/logo/${encodeURIComponent(username)}`}
                        alt=""
                        className="h-5 max-w-[48px] object-contain"
                      />
                    )}
                    <span className="text-xs font-bold text-[#2f3126]">{username}</span>
                    {userData.role === 'admin' ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#2f3126]/10 text-[#2f3126] text-[9px] font-semibold uppercase tracking-wider">Admin</span>
                    ) : (
                      <span className="text-[10px] text-[#77726e]">{userData.company || '—'}</span>
                    )}
                    {userData.company && users.companyFiles?.[userData.company]?.length > 0 && (
                      <span className="text-[10px] text-[#4e6498]">{users.companyFiles[userData.company].length} analyse(s)</span>
                    )}
                    {userData.public && (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#4e6498]/10 text-[#4e6498] text-[9px] font-semibold uppercase tracking-wider">Public</span>
                    )}
                  </div>
                  {/* Row 2: actions */}
                  <div className="flex gap-1 flex-wrap">
                    {userData.role !== 'admin' && (
                      <>
                        <button
                          onClick={() => { setSelectedCompanyFile({ username, userData, isAnalysisAssignment: false }); setShowAssignModal(true) }}
                          className="px-2 py-1 rounded border border-[#4e6498]/40 text-[10px] text-[#4e6498] bg-white hover:bg-[#f6f3ee] transition"
                        >
                          Analyses
                        </button>
                        <button
                          onClick={() => togglePublic(username, userData.public)}
                          className={`px-2 py-1 rounded border text-[10px] bg-white transition ${userData.public ? 'border-[#4e6498]/40 text-[#4e6498] hover:bg-[#f6f3ee]' : 'border-[#77726e]/30 text-[#77726e] hover:bg-[#f6f3ee]'}`}
                          title={userData.public ? 'Rendre privé' : 'Rendre public'}
                        >
                          {userData.public ? '🔓' : '🔒'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setUserFormError(null)
                        setEditUser({
                          username,
                          password: '',
                          role: userData.role,
                          company: userData.company || '',
                          logoData: undefined,
                          currentLogo: userData.logo || null,
                        })
                        setShowEditModal(true)
                      }}
                      disabled={username === 'admin'}
                      className="px-2 py-1 rounded border border-[#77726e]/30 text-[10px] text-[#77726e] bg-white hover:bg-[#f6f3ee] transition disabled:opacity-30"
                    >
                      Édit.
                    </button>
                    <button
                      onClick={() => setUserToDelete(username)}
                      disabled={username === 'admin'}
                      className="px-2 py-1 rounded border border-red-200 text-[10px] text-red-600 bg-white hover:bg-red-50 transition disabled:opacity-30"
                    >
                      Suppr.
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Console */}
          <div className={cardCls}>
            <div className="px-5 pt-4 pb-3 border-b border-[#77726e]/15">
              <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider font-title">Console</h3>
            </div>
            <div className="h-72 overflow-y-auto bg-[#2f3126] rounded-b-[0.625rem] font-title text-xs p-4">
              {logs.length === 0 ? (
                <span className="text-[#77726e]">Waiting for commands_</span>
              ) : (
                logs.map((log, i) => (
                  <div key={i} className={`mb-1 ${
                    log.type === 'error' ? 'text-[#de8da0]' :
                    log.type === 'success' ? 'text-[#a1c977]' :
                    log.type === 'warning' ? 'text-[#f4bd48]' :
                    'text-[#96b4c9]'
                  }`}>
                    <span className="text-[#77726e]">[{log.timestamp}]</span> {log.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Base de données entreprises */}
        <div className={cardCls}>
          <div className="px-6 pt-5 pb-4 border-b border-[#77726e]/15 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider">
              Analyses ({filteredAndSortedCompanies.length})
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-[#77726e]/40 rounded-md px-3 py-1.5 text-xs text-[#2f3126] bg-[#f6f3ee] focus:outline-none focus:ring-1 focus:ring-[#4e6498]/30 w-44"
              />
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => { const [f, o] = e.target.value.split('-'); setSortBy(f); setSortOrder(o) }}
                className="border border-[#77726e]/40 rounded-md px-2 py-1.5 text-xs text-[#2f3126] bg-[#f6f3ee] focus:outline-none"
              >
                <option value="date-desc">Plus récent</option>
                <option value="date-asc">Plus ancien</option>
                <option value="name-asc">Nom A-Z</option>
                <option value="name-desc">Nom Z-A</option>
              </select>
            </div>
          </div>
          <div className="p-6">
            {filteredAndSortedCompanies.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#77726e]">
                {searchTerm ? 'Aucune analyse trouvée' : 'Aucune analyse disponible'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredAndSortedCompanies.map((company, index) => {
                  const name = company.company || filenameToCompanyName(company.filename)
                  const score = company.score || 0
                  const isDeleting = deletingFiles.has(company.filename)
                  const color = getScoreColor(score)

                  return (
                    <div key={index} className={`bg-white border border-[#77726e]/30 rounded-[0.625rem] p-4 transition ${isDeleting ? 'opacity-40' : 'hover:shadow-md'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-[#2f3126] truncate">{name}</h4>
                          <p className="text-[10px] text-[#77726e] mt-0.5 truncate">{company.filename}</p>
                        </div>
                        <span className="ml-2 text-lg font-extrabold font-title flex-shrink-0" style={{ color }}>{score}%</span>
                      </div>

                      {/* Barre score */}
                      <div className="w-full bg-[#d1d6d8] rounded-full h-1.5 mb-3">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
                      </div>

                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleGenerateSummary(company.filename)}
                          className="flex-1 px-2 py-1.5 rounded border border-[#77726e]/30 text-[10px] font-semibold text-[#2f3126] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition"
                        >
                          Synthèse
                        </button>
                        <button
                          onClick={() => completeAnalysis(company.filename)}
                          disabled={isProcessing}
                          title="Génère les pros/cons manquants puis la synthèse, sans reposer de questions"
                          className="flex-1 px-2 py-1.5 rounded border border-[#77726e]/30 text-[10px] font-semibold text-[#2f3126] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition disabled:opacity-30"
                        >
                          Compléter
                        </button>
                        <button
                          onClick={() => { setSelectedCompanyFile({ filename: company.filename, companyData: company, isAnalysisAssignment: true }); setShowAssignModal(true) }}
                          className="flex-1 px-2 py-1.5 rounded border border-[#4e6498]/30 text-[10px] font-semibold text-[#4e6498] bg-white hover:bg-[#f6f3ee] transition"
                        >
                          Assigner
                        </button>
                        <button
                          onClick={() => openJson(company.filename)}
                          className="px-2 py-1.5 rounded border border-[#77726e]/30 text-[10px] text-[#77726e] bg-white hover:bg-[#f6f3ee] transition"
                        >
                          JSON
                        </button>
                        <button
                          onClick={() => setFileToDelete(company.filename)}
                          disabled={isDeleting}
                          className="px-2 py-1.5 rounded border border-red-200 text-[10px] text-red-500 bg-white hover:bg-red-50 transition disabled:opacity-30"
                        >
                          {isDeleting ? '...' : 'Suppr.'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal ajout utilisateur */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className={cardCls + ' p-6 w-full max-w-sm'}>
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-5">Nouvel utilisateur</h3>
            <div className="space-y-3">
              {[
                { label: "Nom d'utilisateur", key: 'username', type: 'text', placeholder: 'username' },
                { label: 'Mot de passe', key: 'password', type: 'password', placeholder: '••••••' },
                { label: 'Entreprise', key: 'company', type: 'text', placeholder: 'Nom de l\'entreprise', disabled: newUser.role === 'admin' },
              ].map(({ label, key, type, placeholder, disabled }) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">{label}</label>
                  <input
                    type={type}
                    value={newUser[key]}
                    onChange={(e) => setNewUser(prev => ({ ...prev, [key]: e.target.value }))}
                    className={inputCls + (disabled ? ' opacity-50' : '')}
                    placeholder={placeholder}
                    disabled={disabled}
                  />
                </div>
              ))}
              <LogoField
                preview={newUser.logoData}
                onFile={(f) => pickLogo(f, (data) => setNewUser(prev => ({ ...prev, logoData: data })))}
                onUrl={(u) => pickLogoFromUrl(u, (data) => setNewUser(prev => ({ ...prev, logoData: data })))}
                onRemove={() => setNewUser(prev => ({ ...prev, logoData: null }))}
                busy={logoBusy}
              />
              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">Rôle</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value, company: e.target.value === 'admin' ? '' : prev.company }))}
                  className={inputCls}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>
            {userFormError && (
              <p className="mt-3 text-xs text-red-600">{userFormError}</p>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={addUser} className={btnPrimary + ' flex-1'}>Créer</button>
              <button onClick={() => { setShowUserModal(false); setUserFormError(null); setNewUser({ username: '', password: '', company: '', role: 'user', logoData: null }) }} className={btnSecondary + ' flex-1'}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal édition utilisateur */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className={cardCls + ' p-6 w-full max-w-sm'}>
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-5">Modifier {editUser.username}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={editUser.password}
                  onChange={(e) => setEditUser(prev => ({ ...prev, password: e.target.value }))}
                  className={inputCls}
                  placeholder="Laisser vide pour ne pas changer"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">Entreprise</label>
                <input
                  type="text"
                  value={editUser.company}
                  onChange={(e) => setEditUser(prev => ({ ...prev, company: e.target.value }))}
                  className={inputCls + (editUser.role === 'admin' ? ' opacity-50' : '')}
                  placeholder="Nom de l'entreprise"
                  disabled={editUser.role === 'admin'}
                />
              </div>
              <LogoField
                preview={
                  editUser.logoData === undefined
                    ? (editUser.currentLogo ? `/api/logo/${encodeURIComponent(editUser.username)}` : null)
                    : editUser.logoData
                }
                onFile={(f) => pickLogo(f, (data) => setEditUser(prev => ({ ...prev, logoData: data })))}
                onUrl={(u) => pickLogoFromUrl(u, (data) => setEditUser(prev => ({ ...prev, logoData: data })))}
                onRemove={() => setEditUser(prev => ({ ...prev, logoData: null }))}
                busy={logoBusy}
              />
              <div>
                <label className="block text-xs font-semibold text-[#77726e] uppercase tracking-wider mb-1">Rôle</label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser(prev => ({ ...prev, role: e.target.value, company: e.target.value === 'admin' ? '' : prev.company }))}
                  className={inputCls}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>
            {userFormError && (
              <p className="mt-3 text-xs text-red-600">{userFormError}</p>
            )}
            <div className="flex gap-2 mt-5">
              <button onClick={updateUser} className={btnPrimary + ' flex-1'}>Enregistrer</button>
              <button onClick={() => { setShowEditModal(false); setUserFormError(null); setEditUser({ username: '', password: '', role: 'user', company: '', logoData: undefined, currentLogo: null }) }} className={btnSecondary + ' flex-1'}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal assignation */}
      {showAssignModal && selectedCompanyFile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className={cardCls + ' p-6 w-full max-w-md max-h-[80vh] flex flex-col'}>
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-4">
              {selectedCompanyFile.isAnalysisAssignment
                ? `Assigner "${selectedCompanyFile.filename}"`
                : `Analyses de ${selectedCompanyFile.username}`}
            </h3>

            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {selectedCompanyFile.isAnalysisAssignment ? (
                Object.entries(users.users || {}).map(([username, userData]) => {
                  if (userData.role === 'admin' || !userData.company) return null
                  const isAssigned = users.companyFiles?.[userData.company]?.includes(selectedCompanyFile.filename) || false
                  return (
                    <div key={username} className="flex items-center justify-between bg-[#f6f3ee] border border-[#77726e]/20 rounded-md px-3 py-2">
                      <div>
                        <div className="text-xs font-bold text-[#2f3126]">{username}</div>
                        <div className="text-[10px] text-[#77726e]">{userData.company}</div>
                      </div>
                      {isAssigned ? (
                        <button onClick={() => unassignAnalysis(selectedCompanyFile.filename, userData.company)} className="px-2 py-1 rounded border border-red-200 text-[10px] text-red-600 bg-white hover:bg-red-50 transition">Retirer</button>
                      ) : (
                        <button onClick={() => assignAnalysisToUser(selectedCompanyFile.filename, username)} className="px-2 py-1 rounded border border-[#39774f]/30 text-[10px] text-[#39774f] bg-white hover:bg-[#f6f3ee] transition">Assigner</button>
                      )}
                    </div>
                  )
                })
              ) : (
                companies.map((company, i) => {
                  const isAssigned = users.companyFiles?.[selectedCompanyFile.userData.company]?.includes(company.filename) || false
                  return (
                    <div key={i} className="flex items-center justify-between bg-[#f6f3ee] border border-[#77726e]/20 rounded-md px-3 py-2">
                      <div>
                        <div className="text-xs font-bold text-[#2f3126]">{company.filename}</div>
                        <div className="text-[10px] text-[#77726e]">{company.company}</div>
                      </div>
                      {isAssigned ? (
                        <button onClick={() => unassignAnalysis(company.filename, selectedCompanyFile.userData.company)} className="px-2 py-1 rounded border border-red-200 text-[10px] text-red-600 bg-white hover:bg-red-50 transition">Retirer</button>
                      ) : (
                        <button onClick={() => assignAnalysisToUser(company.filename, selectedCompanyFile.username)} className="px-2 py-1 rounded border border-[#39774f]/30 text-[10px] text-[#39774f] bg-white hover:bg-[#f6f3ee] transition">Assigner</button>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            <button onClick={() => { setShowAssignModal(false); setSelectedCompanyFile(null) }} className={btnSecondary + ' w-full'}>Fermer</button>
          </div>
        </div>
      )}

      {/* Confirmation suppression utilisateur */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white border border-[#d1d6d8] p-6 w-80 shadow-lg">
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-2">Supprimer l'utilisateur</h3>
            <p className="text-sm text-[#77726e] mb-6">
              Vous allez supprimer <span className="font-semibold text-[#2f3126]">{userToDelete}</span>. Cette action est irreversible.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setUserToDelete(null)} className={btnSecondary}>Annuler</button>
              <button
                onClick={() => { deleteUser(userToDelete); setUserToDelete(null) }}
                className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition uppercase tracking-wider"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation suppression rapport */}
      {fileToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white border border-[#d1d6d8] p-6 w-80 shadow-lg">
            <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-2">Supprimer le rapport</h3>
            <p className="text-sm text-[#77726e] mb-6">
              Vous allez supprimer <span className="font-semibold text-[#2f3126]">{fileToDelete}</span>. Cette action est irreversible.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setFileToDelete(null)} className={btnSecondary}>Annuler</button>
              <button
                onClick={() => { deleteCompany(fileToDelete); setFileToDelete(null) }}
                className="px-4 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition uppercase tracking-wider"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
